import React, { use, useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import { Link } from 'react-router';
import { ModelMap, NewsContext } from '../components/liveloading';
import { NewsModel } from '../models/news';
import { config, frontendRevision } from '../utils/config';
import { logError, sortByDate } from '../utils/misc';

const NewsView: React.FC<{
    readonly newsItem: NewsModel;
}> = ({ newsItem }) => {
    return (
        <>
            <h4>{newsItem.title}</h4>
            <p>
                <Markdown>{newsItem.content}</Markdown>
            </p>
        </>
    );
};

const NewsList: React.FC<{
    readonly news?: ModelMap<NewsModel>;
    readonly loading: boolean;
}> = ({ news, loading }) => {
    if (loading || !news) {
        return <h4>Loading...</h4>;
    }

    const returnValue = Array.from(news.values())
        // eslint-disable-next-line unicorn/no-array-sort
        .sort(sortByDate)
        .map((newsItem) => <NewsView key={newsItem.id} newsItem={newsItem} />);

    return returnValue;
};

export const HomePage: React.FC = () => {
    const { refresh, models } = use(NewsContext);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (loading || models) {
            return;
        }

        // eslint-disable-next-line react-hooks/set-state-in-effect, @eslint-react/set-state-in-effect
        setLoading(true);
        void (async () => {
            try {
                await refresh();
                setLoading(false);
            } catch (error: unknown) {
                logError(error);
            }
        })();
    }, [refresh, loading, models]);

    return (
        <>
            <h1>Home</h1>
            <p>Welcome to foxCaves!</p>
            <h2>News</h2>
            <NewsList loading={loading} news={models} />
            <h2>What is foxCaves?</h2>
            <p>foxCaves is a platform to upload your files and share them with friends as well create links.</p>
            <h2>Useful links</h2>
            <p>
                Pre-made ShareX configurations:{' '}
                <a href="https://git.foxden.network/foxCaves/sharex" rel="noreferrer" target="_blank">
                    https://git.foxden.network/foxCaves/sharex
                </a>
                <br />
                API documentation in OpenAPI format:{' '}
                <a href={`${config.urls.app}/api/v1/openapi.json`} rel="noreferrer" target="_blank">
                    {config.urls.app}/api/v1/openapi.json
                </a>
                <br />
                Source code:{' '}
                <a href="https://git.foxden.network/foxCaves/foxCaves" rel="noreferrer" target="_blank">
                    https://git.foxden.network/foxCaves/foxCaves
                </a>
            </p>
            <h2>Legal</h2>
            <p>
                <Link to="/legal/terms_of_service">Terms of service</Link>
                <br />
                <Link to="/legal/privacy_policy">Privacy policy</Link>
            </p>
            <h2>Version</h2>
            <p>
                Frontend revision: {frontendRevision}
                <br />
                Backend revision: {config.backend_revision}
            </p>
        </>
    );
};
